import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import { AiOutlineArrowLeft } from 'react-icons/ai';
import { FiX } from 'react-icons/fi';
import { useNavigate, Link } from 'react-router-dom';
import { deleteaccount } from "../utils/APIRoutes";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styled from 'styled-components';
import { useServerRole } from '../utils/permissions';
import { getFollowersCount, getFollowersList } from '../services/feed';
import { fetchUsersByIds } from '../services/users';

const UserControl = ({ currentUser, setPopDelete }) => {
  const [password, setPassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();

  const { isAdmin, hasPermission } = useServerRole();

  // ---- Role-gated admin links ----
  // Each entry maps 1:1 to a permission in ADMIN_PERMISSIONS
  // (models/userModel.js) — adding a new permission there later just
  // means adding one more row here. `to` paths are best-guess routes
  // matching each permission's actual backend endpoint (manage_orders
  // -> /admin/orders, etc.) — adjust any that don't match your actual
  // router. superadmin/admin-with-permission both satisfy `isAdmin`
  // from useServerRole(), so this reads the same for both, same as
  // the rest of the app's gating.
  const adminLinks = [
    {
      key: 'manage_users',
      label: 'Control Panel',
      to: `/userrequests/${currentUser?._id}`,
      show: isAdmin && hasPermission('manage_users'),
    },
    {
      key: 'manage_products',
      label: 'Manage Products',
      to: `/traderbrand/${currentUser?._id}`,
      show: isAdmin && hasPermission('manage_products'),
    },
    {
      key: 'manage_orders',
      label: 'Manage Orders',
      to: `/orders/${currentUser?._id}`,
      show: isAdmin && hasPermission('manage_orders'),
    },
    {
      key: 'view_all_orders',
      label: 'All Orders',
      to: `/orders/${currentUser?._id}`,
      // Only show this separately from "Manage Orders" for admins who
      // have view_all_orders but NOT manage_orders (read-only access)
      // — an admin with manage_orders already sees everything via the
      // Manage Orders link above, so this avoids a redundant duplicate.
      show: isAdmin && hasPermission('view_all_orders') && !hasPermission('manage_orders'),
    },
    {
      key: 'manage_subscriptions',
      label: 'Subscriptions Admin',
      to: `/subscription/${currentUser?._id}`,
      show: isAdmin && hasPermission('manage_subscriptions'),
    },
    {
      key: 'manage_agents',
      label: 'Manage Agents',
      to: `/agents/${currentUser?._id}`,
      show: isAdmin && hasPermission('manage_agents'),
    },
    {
      key: 'send_emails',
      label: 'Send Bulk Email',
      to: `/emails/${currentUser?._id}`,
      show: isAdmin && hasPermission('send_emails'),
    },
    {
      key: 'view_insights',
      label: 'Insights',
      to: `/insights/${currentUser?._id}`,
      show: isAdmin && hasPermission('view_insights'),
    },
  ].filter((link) => link.show);

  // ---- Own followers count + list ----
  const [followersCount, setFollowersCount] = useState(null);
  const [showFollowersList, setShowFollowersList] = useState(false);
  const [followersListUsers, setFollowersListUsers] = useState([]);
  const [loadingFollowersList, setLoadingFollowersList] = useState(false);

  useEffect(() => {
    if (!currentUser?._id) return;

    let cancelled = false;
    getFollowersCount(currentUser._id)
      .then((count) => {
        if (!cancelled) setFollowersCount(count);
      })
      .catch((err) => console.error('Followers count fetch failed:', err));

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const handleFollowersCountClick = async () => {
    if (!currentUser?._id) return;
    setShowFollowersList(true);
    setLoadingFollowersList(true);
    setFollowersListUsers([]);

    try {
      const ids = await getFollowersList(currentUser._id);
      const users = await fetchUsersByIds(ids);
      setFollowersListUsers(users);
    } catch (err) {
      console.error('Failed to load followers list:', err);
    } finally {
      setLoadingFollowersList(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    setIsDeleting(true);
    try {
      const { data } = await apiClient.post(deleteaccount, {
        userId: currentUser._id,
        password: password
      });

      if (data.status) {
        toast.success('Account deleted successfully');
        localStorage.clear();
        setTimeout(() => navigate('/login'), 2000);
      } else {
        toast.error(data.msg || 'Failed to delete account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.response?.data?.msg || 'Error deleting account');
    } finally {
      setIsDeleting(false);
      setShowConfirmation(false);
    }
  };

  return (
    <DeleteAccountContainer>
      <TopRow>
        <CloseButton onClick={() => setPopDelete(false)}><AiOutlineArrowLeft/></CloseButton>

        <FollowersIndicator onClick={handleFollowersCountClick}>
          {followersCount === null ? 'Loading followers…' : `${followersCount} follower${followersCount === 1 ? '' : 's'}`}
        </FollowersIndicator>
      </TopRow>

      <LinksGrid>
        <LinkPill as={Link} to={`/subscription/${currentUser?._id}`}>
          View Subscription
        </LinkPill>

        {adminLinks.map((link) => (
          <LinkPill as={Link} to={link.to} key={link.key} $admin>
            {link.label}
          </LinkPill>
        ))}
      </LinksGrid>

      <WarningText>
        Warning: This action is permanent and cannot be undone. All your data will be deleted.
      </WarningText>

      {!currentUser?.isGoogleUser && showConfirmation && (
        <PasswordConfirmation>
          <label htmlFor="password">Enter your password to confirm:</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
            autoComplete="current-password"
          />
        </PasswordConfirmation>
      )}

      <DeleteButton
        onClick={handleDeleteAccount}
        disabled
      >
        {isDeleting
          ? 'Deleting...'
          : showConfirmation
            ? 'Confirm Deletion'
            : 'Delete My Account'}
      </DeleteButton>

      {showConfirmation && (
        <CancelButton onClick={() => setShowConfirmation(false)}>
          Cancel
        </CancelButton>
      )}

      {showFollowersList && (
        <FollowersListOverlay onClick={() => setShowFollowersList(false)}>
          <FollowersListModal onClick={(e) => e.stopPropagation()}>
            <FollowersListHeader>
              <h4>Your followers</h4>
              <CloseListButton onClick={() => setShowFollowersList(false)}>
                <FiX size={18} />
              </CloseListButton>
            </FollowersListHeader>

            <FollowersListBody>
              {loadingFollowersList ? (
                <FollowersListStatus>Loading...</FollowersListStatus>
              ) : followersListUsers.length === 0 ? (
                <FollowersListStatus>No one yet.</FollowersListStatus>
              ) : (
                followersListUsers.map((u) => (
                  <FollowersListRow key={u._id}>
                    {u.avatarImage ? (
                      <FollowersListAvatar
                        as="img"
                        src={u.avatarImage.startsWith('http') ? u.avatarImage : `data:image/svg+xml;base64,${u.avatarImage}`}
                        alt={u.username}
                      />
                    ) : (
                      <FollowersListAvatar placeholder>
                        {u.username?.charAt(0)?.toUpperCase()}
                      </FollowersListAvatar>
                    )}
                    <FollowersListName>{u.username}</FollowersListName>
                  </FollowersListRow>
                ))
              )}
            </FollowersListBody>
          </FollowersListModal>
        </FollowersListOverlay>
      )}

      <ToastContainer />
    </DeleteAccountContainer>
  );
};

export default UserControl;

/* ================= STYLES ================= */

const DeleteAccountContainer = styled.div`
  position: relative;
  max-width: 500px;
  width: 90%;
  margin: 0 auto;
  padding: 1.5rem;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  max-height: 80vh;
  overflow-y: auto;

  /* Bottom-sheet layout on mobile: fixed to the viewport's bottom
     edge, filling 70vh, sliding out of the fixed overlay Categories.js
     already renders this inside of (position: fixed; inset: 0;
     rgba(0,0,0,0.5) backdrop) rather than sitting centered on it. */
  @media screen and (max-width: 768px) {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    top: auto;
    width: 100%;
    max-width: 100%;
    height: 70vh;
    max-height: 70vh;
    margin: 0;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.18);
    padding: 1.25rem 1.25rem calc(1.25rem + env(safe-area-inset-bottom, 0px));
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const FollowersIndicator = styled.span`
  font-size: 0.85rem;
  color: #111;
  cursor: pointer;
  text-decoration: none;
`;

const WarningText = styled.p`
  color: #d32f2f;
  margin-bottom: 1.5rem;
`;

const PasswordConfirmation = styled.div`
  margin: 1rem 0;

  input {
    width: 100%;
    padding: 0.5rem;
    margin-top: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
`;

const DeleteButton = styled.button`
  background-color: #d32f2f;
  color: white;
  border: none;
  padding: 0.7rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.3s;

  &:hover {
    background-color: #b71c1c;
  }

  &:disabled {
    background-color: #9e9e9e;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  margin-left: 1rem;
  background-color: #f5f5f5;
  border: 1px solid #ccc;
  padding: 0.7rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #e0e0e0;
  }
`;

// Replaces the old pipe-separated HeaderContainer — wraps naturally
// on narrow screens instead of overflowing, and every link renders as
// a consistent pill instead of raw text with "||" separators.
const LinksGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0.75rem 0 1.25rem;
`;

const LinkPill = styled.span`
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 12px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;

  /* Admin-only links get a visually distinct treatment so they read
     as a different tier of action from "View Subscription". */
  background: ${(props) => (props.$admin ? '#0f1f3d' : '#f1f1f1')};
  color: ${(props) => (props.$admin ? '#fff' : '#111')};
`;

const CloseButton = styled.p`
  margin: 0;
  cursor: pointer;
`;

const FollowersListOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1500;
`;

const FollowersListModal = styled.div`
  width: 85%;
  max-width: 340px;
  max-height: 70vh;
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  overflow-y: auto;
`;

const FollowersListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h4 {
    margin: 0;
    font-size: 15px;
  }
`;

const CloseListButton = styled.span`
  cursor: pointer;
  display: flex;
`;

const FollowersListStatus = styled.p`
  text-align: center;
  color: #888;
  font-size: 13px;
`;

const FollowersListBody = styled.div``;

const FollowersListRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const FollowersListAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  ${(props) =>
    props.$placeholder &&
    `
    background: #4f8cff;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
  `}
`;

const FollowersListName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;